'use client';
import { TLocation } from '@/app/(blogposts)/blog/blog.types';
import { Typography } from '@/components/ui/Text';
import { assetUrl } from '@/lib/directus/assetUrl';
import Image from 'next/image';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import './BlogArticleModuleCentersCardMobile.scss';

type Props = {
  location: TLocation;
  onClick: () => void;
  selected: boolean;
};

export const BlogArticleModuleCentersCardMobile: React.FC<Props> = ({
  location: { previewImage, label, clinics },
  onClick,
  selected,
}) => {
  const [currentHeight, setCurrentHeight] = useState<number | undefined>(
    undefined
  );
  const mainDivRef = useRef<null | HTMLDivElement>(null);
  const centerPreviewRef = useRef<null | HTMLDivElement>(null);

  const getHeight = useCallback(() => {
    if (!selected) return;

    const { current: subElement } = centerPreviewRef;

    if (!subElement) return;

    const { height: subElementHeight } = subElement.getBoundingClientRect();

    const mainElementHeight = (() => {
      const { current: element } = mainDivRef;

      if (!element) return 0;

      const { height } = element.getBoundingClientRect();

      return height;
    })();

    return subElementHeight + mainElementHeight;
  }, [selected]);

  useLayoutEffect(() => {
    const calculateHeight = () => {
      const calculatedHeight = getHeight();

      setCurrentHeight(calculatedHeight);
    };

    calculateHeight();
  }, [getHeight]);

  return (
    <div
      className={clsx('BlogArticleModuleCentersCardMobile', {
        'BlogArticleModuleCentersCardMobile--selected': selected,
      })}
      onClick={selected ? undefined : onClick}
      ref={mainDivRef}
      style={{ height: currentHeight }}
    >
      <Image
        src={assetUrl(previewImage) || ''}
        alt='center preview'
        fill
        className='BlogArticleModuleCentersCardMobile__image'
      />
      <div className='BlogArticleModuleCentersCardMobile__image-overlay' />

      <div className='BlogArticleModuleCentersCardMobile__content'>
        <Typography
          className='BlogArticleModuleCentersCardMobile__title'
          variant='body'
        >
          {label}
        </Typography>

        <Typography
          className='BlogArticleModuleCentersCardMobile__subtitle'
          variant='body-small'
        >
          {`${clinics.length} clinics`}
        </Typography>
      </div>

      <div
        className='BlogArticleModuleCentersCardMobile__icon-wrapper'
        onClick={selected ? onClick : undefined}
      >
        <div className='BlogArticleModuleCentersCardMobile__icon-content'>
          <div className='BlogArticleModuleCentersCardMobile__white-circle'>
            {!selected && (
              <svg
                className='BlogArticleModuleCentersCardMobile__white-circle-content'
                width='14'
                height='14'
                viewBox='0 0 14 14'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M6.98649 1L6.9985 13M13 7.0015L1 7.0015'
                  stroke='#292722'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}

            {selected && (
              <svg
                className='BlogArticleModuleCentersCardMobile__white-circle-content'
                width='14'
                height='2'
                viewBox='0 0 14 2'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M13 1L1 1'
                  stroke='#292722'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </div>
        </div>
      </div>

      {selected && (
        <div
          className='BlogArticleModuleCentersCardMobile__center-preview'
          ref={centerPreviewRef}
        >
          {clinics.map(({ id, name, address }, index) => (
            <>
              {index > 0 && <hr />}

              <div
                className='BlogArticleModuleCentersCardMobile__center'
                key={id}
              >
                <div className='BlogArticleModuleCentersCardMobile_center-name'>
                  {name}
                </div>

                <div className='BlogArticleModuleCentersCardMobile__center-address'>
                  {address}
                </div>
              </div>
            </>
          ))}
        </div>
      )}
    </div>
  );
};
